import { useState, useEffect, useCallback } from 'react';
import { authController } from '../../controllers/authController';
import { CheckCircle, Sparkles, Eye, EyeOff, MapPin, Bell, AlertTriangle } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { collectAuthExtras } from '../../utils/authExtras'; // 🆕
import OtpVerification from './Otpverification'; // 🆕 OTP / phone number step

/* ─────────────────────────────────────────────
   Forgot Password Modal
───────────────────────────────────────────── */
const fpStyles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 4000,
  },
  card: {
    backgroundColor: '#fff', borderRadius: '14px', padding: '36px',
    width: '90%', maxWidth: '380px', position: 'relative',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  },
  close: {
    position: 'absolute', top: '12px', right: '16px',
    background: 'transparent', border: 'none',
    fontSize: '22px', cursor: 'pointer', color: '#6b7280',
  },
  title: { fontSize: '22px', fontWeight: '700', color: '#1f2937', marginBottom: '8px', margin: '0 0 8px 0' },
  sub: { fontSize: '14px', color: '#6b7280', marginBottom: '20px', lineHeight: '1.5' },
  input: {
    padding: '12px 16px', border: '1px solid #d1d5db',
    borderRadius: '8px', fontSize: '15px', outline: 'none',
    transition: 'border-color 0.2s', width: '100%', boxSizing: 'border-box',
  },
  btn: {
    backgroundColor: '#2563eb', color: '#fff', padding: '13px',
    borderRadius: '8px', border: 'none', fontWeight: '600',
    fontSize: '15px', cursor: 'pointer', width: '100%',
  },
  btnDisabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
  error: {
    backgroundColor: '#fee2e2', color: '#dc2626',
    padding: '10px', borderRadius: '8px', fontSize: '13px',
    textAlign: 'center', marginBottom: '12px',
  },
  sentIcon: { fontSize: '48px', marginBottom: '16px', textAlign: 'center' },
};

const ForgotPasswordModal = ({ onClose }) => {
  const [fpEmail, setFpEmail]     = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpSent, setFpSent]       = useState(false);
  const [fpError, setFpError]     = useState('');

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!fpEmail) { setFpError('Please enter your email'); return; }
    setFpLoading(true); setFpError('');
    try {
      await authController.forgotPassword(fpEmail);
      setFpSent(true);
    } catch {
      setFpError('Something went wrong. Please try again.');
    }
    setFpLoading(false);
  };

  return (
    <div style={fpStyles.overlay} onClick={onClose}>
      <div style={fpStyles.card} onClick={e => e.stopPropagation()}>
        <button style={fpStyles.close} onClick={onClose}>×</button>
        {fpSent ? (
          <div style={{ textAlign: 'center' }}>
            <div style={fpStyles.sentIcon}>✉️</div>
            <h3 style={fpStyles.title}>Check your inbox!</h3>
            <p style={fpStyles.sub}>
              We've sent a reset link to<br /><strong>{fpEmail}</strong>
            </p>
            <button style={fpStyles.btn} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <h3 style={fpStyles.title}>Forgot Password?</h3>
            <p style={fpStyles.sub}>Enter your email and we'll send you a password reset link.</p>
            {fpError && <div style={fpStyles.error}>{fpError}</div>}
            <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <input
                type="email"
                placeholder="your@email.com"
                value={fpEmail}
                onChange={e => setFpEmail(e.target.value)}
                disabled={fpLoading}
                style={fpStyles.input}
                onFocus={e => (e.target.style.borderColor = '#2563eb')}
                onBlur={e => (e.target.style.borderColor = '#d1d5db')}
              />
              <button
                type="submit"
                disabled={fpLoading}
                style={{ ...fpStyles.btn, ...(fpLoading ? fpStyles.btnDisabled : {}) }}
              >
                {fpLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Google icon
───────────────────────────────────────────── */
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

/* ─────────────────────────────────────────────
   Permission Gate — location + notification
   Jab tak dono allow nahi ho jate, koi bhi
   auth button (Login / Google) clickable nahi hoga
───────────────────────────────────────────── */
const permStyles = {
  wrap: {
    border: '1px solid #fde68a', backgroundColor: '#fffbeb',
    borderRadius: '10px', padding: '14px 16px', marginBottom: '18px',
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  headRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  headText: { fontSize: '13.5px', fontWeight: '600', color: '#92400e', margin: 0 },
  row: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '10px', backgroundColor: '#fff', border: '1px solid #f3e8c8',
    borderRadius: '8px', padding: '10px 12px',
  },
  rowLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  rowLabel: { fontSize: '14px', color: '#374151', fontWeight: '500' },
  rowSub: { fontSize: '12px', color: '#9ca3af', marginTop: '2px' },
  grantedPill: {
    display: 'flex', alignItems: 'center', gap: '4px',
    color: '#059669', fontSize: '13px', fontWeight: '600',
  },
  allowBtn: {
    backgroundColor: '#2563eb', color: '#fff', border: 'none',
    borderRadius: '6px', padding: '7px 14px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  deniedPill: {
    color: '#dc2626', fontSize: '12.5px', fontWeight: '600',
  },
};

const PermissionRow = ({ icon, label, subLabel, status, onAllow }) => {
  return (
    <div style={permStyles.row}>
      <div style={permStyles.rowLeft}>
        {icon}
        <div>
          <div style={permStyles.rowLabel}>{label}</div>
          {status === 'denied' && <div style={permStyles.rowSub}>Blocked — allow it from your browser's site settings, then retry</div>}
          {status !== 'denied' && subLabel && <div style={permStyles.rowSub}>{subLabel}</div>}
        </div>
      </div>

      {status === 'granted' && (
        <span style={permStyles.grantedPill}><CheckCircle size={16} /> Allowed</span>
      )}
      {status === 'denied' && (
        <button type="button" style={{ ...permStyles.allowBtn, backgroundColor: '#dc2626' }} onClick={onAllow}>
          Retry
        </button>
      )}
      {(status === 'prompt' || status === 'checking') && (
        <button type="button" style={permStyles.allowBtn} onClick={onAllow} disabled={status === 'checking'}>
          Allow
        </button>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main Login Component
───────────────────────────────────────────── */
const Login = ({ isOpen, onClose, onSwitchToRegister, onLoginSuccess }) => {
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe]     = useState(false);
  const [loading, setLoading]           = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [userName, setUserName]         = useState('');
  const [showForgot, setShowForgot]     = useState(false);

  // 🆕 OTP step: backend jab token ki jagah challenge de (number par OTP gaya, ya Google user ka number chahiye)
  // challenge = { challengeId, phoneMasked?, phoneRequired?, retryAfterSeconds? }
  const [challenge, setChallenge]       = useState(null);

  // 🆕 Location + Notification permission gate
  // status values: 'checking' | 'granted' | 'denied' | 'prompt' | 'unsupported'
  const [locStatus, setLocStatus]     = useState('checking');
  const [notifStatus, setNotifStatus] = useState('checking');

  // 🆕 Modal band ho to OTP step reset karo
  useEffect(() => {
    if (!isOpen) setChallenge(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) { setEmail(savedEmail); setRememberMe(true); }

    // ── Notification permission (sync read, live via 'notifications' Permissions API where supported)
    if (typeof Notification === 'undefined') {
      setNotifStatus('unsupported');
    } else {
      setNotifStatus(Notification.permission === 'default' ? 'prompt' : Notification.permission);
    }

    let notifPermStatus;
    let geoPermStatus;

    if (navigator.permissions && navigator.permissions.query) {
      // Notifications
      navigator.permissions.query({ name: 'notifications' })
        .then((status) => {
          notifPermStatus = status;
          setNotifStatus(status.state === 'prompt' ? 'prompt' : status.state);
          status.onchange = () => setNotifStatus(status.state === 'prompt' ? 'prompt' : status.state);
        })
        .catch(() => {});

      // Geolocation
      navigator.permissions.query({ name: 'geolocation' })
        .then((status) => {
          geoPermStatus = status;
          setLocStatus(status.state);
          status.onchange = () => setLocStatus(status.state);
        })
        .catch(() => setLocStatus(navigator.geolocation ? 'prompt' : 'unsupported'));
    } else {
      setLocStatus(navigator.geolocation ? 'prompt' : 'unsupported');
    }

    return () => {
      if (notifPermStatus) notifPermStatus.onchange = null;
      if (geoPermStatus) geoPermStatus.onchange = null;
    };
  }, [isOpen]);

  const requestLocationAccess = useCallback(() => {
    if (!navigator.geolocation) { setLocStatus('unsupported'); return; }
    setLocStatus('checking');
    navigator.geolocation.getCurrentPosition(
      () => setLocStatus('granted'),
      (err) => setLocStatus(err.code === 1 ? 'denied' : 'prompt'),
      { timeout: 10000 }
    );
  }, []);

  const requestNotificationAccess = useCallback(async () => {
    if (typeof Notification === 'undefined') { setNotifStatus('unsupported'); return; }
    setNotifStatus('checking');
    try {
      const result = await Notification.requestPermission();
      setNotifStatus(result === 'default' ? 'prompt' : result);
    } catch {
      setNotifStatus('prompt');
    }
  }, []);

  // 'unsupported' ko blocking nahi maante — us browser/device par woh permission hai hi nahi
  const locOk    = locStatus === 'granted' || locStatus === 'unsupported';
  const notifOk  = notifStatus === 'granted' || notifStatus === 'unsupported';
  const permissionsReady = locOk && notifOk;

  const createConfetti = () => {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7', '#a29bfe'];
    return Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      animationDelay: Math.random() * 3,
      backgroundColor: colors[Math.floor(Math.random() * colors.length)],
    }));
  };

  const triggerSuccess = (name) => {
    setUserName(name || 'User');
    setSuccess(true);
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3000);
    setTimeout(() => {
      setEmail(''); setPassword(''); setSuccess(false);
      setChallenge(null);
      onClose();
    }, 2500);
  };

  // 🆕 Login poora hua (seedha ya OTP verify ke baad) — parent ko user do aur welcome animation dikhao
  const finishLogin = (user) => {
    if (onLoginSuccess && user) onLoginSuccess(user);
    triggerSuccess(user?.firstName || user?.fullName || user?.name);
  };

  /* ── Email / Password submit — deviceId/address/fcmToken bhi bhejta hai ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!permissionsReady) { setError('Please allow location & notification access to continue'); return; }
    if (!email || !password) { setError('Please fill in all fields'); return; }
    setLoading(true); setError('');

    if (rememberMe) localStorage.setItem('rememberedEmail', email);
    else localStorage.removeItem('rememberedEmail');

    // 🆕 deviceId, deviceName, deviceType, address (permission based), fcmToken (agar mile)
    const extra = await collectAuthExtras();

    const result = await authController.login(email, password, extra);

    if (result.success && result.otpRequired) {
      // 🆕 Number hai → OTP gaya. Token abhi nahi mila, OTP verify screen dikhao
      setChallenge(result.data);
    } else if (result.success) {
      finishLogin(result.data.user);
    } else {
      setError(result.message || 'Login failed. Please try again.');
    }
    setLoading(false);
  };

  /* ── Google login — yahan bhi deviceId/address/fcmToken bhejta hai ── */
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true); setError('');
      try {
        const profileRes = await fetch(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        );
        if (!profileRes.ok) throw new Error('Google profile fetch failed');
        const profile = await profileRes.json();

        // 🆕 deviceId, deviceName, deviceType, address, fcmToken collect karo
        const extra = await collectAuthExtras();

        const result = await authController.loginWithGoogle({
          googleId:  profile.sub,
          email:     profile.email,
          firstName: profile.given_name || profile.name?.split(' ')[0] || 'User',
          lastName:  profile.family_name || '',
          avatar:    profile.picture,
          profile,
          ...extra,
        });

        if (result?.success && result.otpRequired) {
          // 🆕 Number hai → OTP screen; number nahi → (data.phoneRequired) number maangne wali screen
          setChallenge(result.data);
        } else if (result?.success) {
          finishLogin(result.data?.user);
        } else {
          setError(result?.message || 'Google login failed. Please try again.');
        }
      } catch (err) {
        console.error('[Google Login]', err);
        setError('Google login failed. Please try again.');
      }
      setGoogleLoading(false);
    },
    onError: () => {
      setError('Google login was cancelled or failed.');
      setGoogleLoading(false);
    },
  });

  const handleGoogleLogin = () => {
    if (!permissionsReady) { setError('Please allow location & notification access to continue'); return; }
    setGoogleLoading(true);
    googleLogin();
  };

  if (!isOpen) return null;

  const anyLoading = loading || googleLoading;
  const actionsDisabled = anyLoading || !permissionsReady;

  const s = {
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 2000,
    },
    modal: {
      backgroundColor: '#fff', borderRadius: '14px',
      padding: '40px', width: '90%', maxWidth: '420px',
      boxShadow: '0 20px 40px rgba(0,0,0,0.15)', position: 'relative',
    },
    closeBtn: {
      position: 'absolute', top: '14px', right: '16px',
      background: 'transparent', border: 'none',
      fontSize: '24px', cursor: 'pointer', color: '#6b7280',
    },
    title: { fontSize: '28px', fontWeight: '700', color: '#1f2937', marginBottom: '24px', textAlign: 'center', margin: '0 0 24px 0' },
    errorBox: {
      backgroundColor: '#fee2e2', color: '#dc2626',
      padding: '12px', borderRadius: '8px',
      marginBottom: '16px', fontSize: '14px', textAlign: 'center',
    },
    form: { display: 'flex', flexDirection: 'column', gap: '18px' },
    group: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: {
      padding: '12px 16px', border: '1px solid #d1d5db', borderRadius: '8px',
      fontSize: '15px', outline: 'none', transition: 'border-color 0.2s',
      width: '100%', boxSizing: 'border-box',
    },
    pwWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
    pwInput: {
      padding: '12px 46px 12px 16px', border: '1px solid #d1d5db', borderRadius: '8px',
      fontSize: '15px', outline: 'none', transition: 'border-color 0.2s',
      width: '100%', boxSizing: 'border-box',
    },
    eyeBtn: {
      position: 'absolute', right: '12px', background: 'transparent',
      border: 'none', cursor: 'pointer', color: '#6b7280',
      display: 'flex', alignItems: 'center', padding: '4px',
    },
    rowBetween: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    rememberLabel: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151', cursor: 'pointer' },
    checkbox: { width: '16px', height: '16px', cursor: 'pointer', accentColor: '#2563eb' },
    forgotBtn: {
      background: 'transparent', border: 'none', color: '#2563eb',
      fontSize: '14px', fontWeight: '500', cursor: 'pointer', textDecoration: 'underline', padding: 0,
    },
    loginBtn: {
      backgroundColor: '#2563eb', color: '#fff', padding: '14px', borderRadius: '8px',
      border: 'none', fontWeight: '600', fontSize: '16px', cursor: 'pointer',
      transition: 'background-color 0.2s', marginTop: '2px',
    },
    btnDisabled: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
    divider: { display: 'flex', alignItems: 'center', gap: '12px', color: '#9ca3af', fontSize: '13px', margin: '4px 0' },
    divLine: { flex: 1, height: '1px', backgroundColor: '#e5e7eb' },
    googleBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
      padding: '13px', borderRadius: '8px', border: '1.5px solid #d1d5db',
      backgroundColor: '#fff', fontSize: '15px', fontWeight: '600', color: '#374151',
      cursor: 'pointer', transition: 'background-color 0.2s, border-color 0.2s', width: '100%',
    },
    regRow: { textAlign: 'center', marginTop: '18px', color: '#6b7280', fontSize: '14px' },
    regBtn: { background: 'transparent', border: 'none', color: '#2563eb', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' },
    successOverlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 3000, animation: 'fadeIn 0.3s ease-in',
    },
    successCard: {
      backgroundColor: '#fff', borderRadius: '20px', padding: '48px 40px',
      maxWidth: '450px', width: '90%', textAlign: 'center',
      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
      position: 'relative', overflow: 'hidden',
      animation: 'scaleIn 0.5s cubic-bezier(0.68,-0.55,0.265,1.55)',
    },
    successIcon: {
      width: '80px', height: '80px', backgroundColor: '#10b981',
      borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 24px', animation: 'bounceIn 0.6s ease-out 0.2s both',
    },
    successTitle: { fontSize: '32px', fontWeight: '700', color: '#1f2937', marginBottom: '16px', animation: 'slideUp 0.5s ease-out 0.3s both' },
    successMsg:   { fontSize: '18px', color: '#6b7280', lineHeight: '1.6', marginBottom: '8px', animation: 'slideUp 0.5s ease-out 0.4s both' },
    successSub:   { fontSize: '16px', color: '#9ca3af', animation: 'slideUp 0.5s ease-out 0.5s both' },
    sparkle:      { display: 'inline-block', animation: 'rotate 2s linear infinite' },
    confetti:     { position: 'absolute', width: '10px', height: '10px', top: '-10px', opacity: 0, animation: 'confettiFall 3s ease-out forwards' },
  };

  const css = `
    @keyframes fadeIn    { from{opacity:0} to{opacity:1} }
    @keyframes scaleIn   { from{transform:scale(0.5);opacity:0} to{transform:scale(1);opacity:1} }
    @keyframes bounceIn  { 0%{transform:scale(0)} 50%{transform:scale(1.1)} 100%{transform:scale(1)} }
    @keyframes slideUp   { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }
    @keyframes rotate    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    @keyframes confettiFall { 0%{top:-10px;opacity:1} 100%{top:100vh;opacity:0;transform:translateX(60px) rotate(360deg)} }
  `;

  return (
    <>
      <style>{css}</style>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}

      <div style={s.overlay} onClick={onClose}>
        <div style={s.modal} onClick={e => e.stopPropagation()}>
          <button style={s.closeBtn} onClick={onClose}>×</button>

          {/* 🆕 OTP step: number par OTP gaya hai (ya Google user se number maanga ja raha hai) */}
          {challenge ? (
            <OtpVerification
              challenge={challenge}
              backLabel="← Back to login"
              onVerified={(result) => finishLogin(result.data?.user)}
              onExpired={() => {
                setChallenge(null);
                setError('OTP session expired. Please login again.');
              }}
              onBack={() => { setChallenge(null); setError(''); }}
            />
          ) : (
            <>
              <h2 style={s.title}>Login</h2>

              {error && <div style={s.errorBox}>{error}</div>}

              {/* 🆕 Permission gate — jab tak location + notification allow nahi, aage ke buttons disabled */}
              {!permissionsReady && (
                <div style={permStyles.wrap}>
                  <div style={permStyles.headRow}>
                    <AlertTriangle size={16} color="#92400e" />
                    <p style={permStyles.headText}>Please allow these permissions to continue</p>
                  </div>

                  <PermissionRow
                    icon={<MapPin size={18} color="#2563eb" />}
                    label="Location access"
                    subLabel="Needed to verify login location"
                    status={locStatus}
                    onAllow={requestLocationAccess}
                  />
                  <PermissionRow
                    icon={<Bell size={18} color="#2563eb" />}
                    label="Notification access"
                    subLabel="Needed for login alerts"
                    status={notifStatus}
                    onAllow={requestNotificationAccess}
                  />
                </div>
              )}

              <form style={s.form} onSubmit={handleSubmit}>
                <div style={s.group}>
                  <label style={s.label}>Email</label>
                  <input
                    type="email"
                    style={s.input}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    disabled={anyLoading}
                    onFocus={e => (e.target.style.borderColor = '#2563eb')}
                    onBlur={e => (e.target.style.borderColor = '#d1d5db')}
                  />
                </div>

                <div style={s.group}>
                  <label style={s.label}>Password</label>
                  <div style={s.pwWrap}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      style={s.pwInput}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      disabled={anyLoading}
                      onFocus={e => (e.target.style.borderColor = '#2563eb')}
                      onBlur={e => (e.target.style.borderColor = '#d1d5db')}
                    />
                    <button
                      type="button"
                      style={s.eyeBtn}
                      onClick={() => setShowPassword(v => !v)}
                      tabIndex={-1}
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div style={s.rowBetween}>
                  <label style={s.rememberLabel}>
                    <input
                      type="checkbox"
                      style={s.checkbox}
                      checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)}
                      disabled={anyLoading}
                    />
                    Remember me
                  </label>
                  <button
                    type="button"
                    style={s.forgotBtn}
                    onClick={() => setShowForgot(true)}
                    disabled={actionsDisabled}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={actionsDisabled}
                  style={{ ...s.loginBtn, ...(actionsDisabled ? s.btnDisabled : {}) }}
                  onMouseEnter={e => { if (!actionsDisabled) e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
                  title={!permissionsReady ? 'Allow location & notification access first' : undefined}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
              </form>

              <div style={s.divider}>
                <div style={s.divLine} />
                or continue with
                <div style={s.divLine} />
              </div>

              <button
                style={{ ...s.googleBtn, ...(actionsDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
                onClick={handleGoogleLogin}
                disabled={actionsDisabled}
                title={!permissionsReady ? 'Allow location & notification access first' : undefined}
                onMouseEnter={e => { if (!actionsDisabled) { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.borderColor = '#9ca3af'; } }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.borderColor = '#d1d5db'; }}
              >
                <GoogleIcon />
                {googleLoading ? 'Connecting to Google...' : 'Continue with Google'}
              </button>

              <div style={s.regRow}>
                Don't have an account?{' '}
                <button style={s.regBtn} onClick={onSwitchToRegister} disabled={actionsDisabled}>
                  Register here
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {success && (
        <div style={s.successOverlay}>
          <div style={s.successCard}>
            {showConfetti && createConfetti().map(c => (
              <div key={c.id} style={{ ...s.confetti, left: `${c.left}%`, backgroundColor: c.backgroundColor, animationDelay: `${c.animationDelay}s` }} />
            ))}
            <div style={s.successIcon}>
              <CheckCircle size={48} color="#ffffff" strokeWidth={3} />
            </div>
            <h2 style={s.successTitle}>🎉 Welcome Back! 🎉</h2>
            <p style={s.successMsg}>Hey {userName}! You've successfully logged in.</p>
            <p style={s.successSub}>
              Redirecting you to dashboard...
              <br />
              <span style={s.sparkle}><Sparkles size={20} color="#f59e0b" /></span>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;